/**
 * FractureSkillEffect — Conjuration Tether Fork 2 active: shade splits into three.
 *
 * Instant skill that destroys any existing Tether/Mirror shades and spawns
 * 3 shades at 120° intervals around the player. Each shade deals 50% of the
 * player's base damage (resolver-overridable via Splinter upgrade to 65%)
 * and shares the player's HP pool.
 *
 * Formation geometry: shadeAngle[i] = (i × 120°) + playerFacingAngle
 * Shade positions: player.pos + rotatedVector(i) × effectiveRadius
 * where effectiveRadius = mirrorDistanceTiles × mirrorAngleSpread × tileSize
 *
 * On activation, all existing shades (from Tether, Mirror, or a prior Fracture)
 * are destroyed before the new formation spawns.
 *
 * Upgrade hooks (resolved at activation via UpgradeEffectResolver, 'fracture' scope):
 * - Splinter (fracture_upgrade_1): shadeDamageMultiplier max 0.65,
 *   mirrorAngleSpread max 1.25 (radius multiplier, not angle)
 *
 * Long cooldown tier (2000ms). Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Fracture
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { ShadeSummonConfig } from './TetherSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface FractureDeps {
  readonly resolver: {
    getMaxEffect(skillId: string, key: string): number | undefined;
    getMultiplicativeEffect(skillId: string, key: string): number;
  };
  readonly player: {
    getPosition(): { x: number; y: number };
    getFacingDirection(): { x: number; y: number };
    getBaseDamage(): number;
  };
  readonly targeting: {
    shouldFizzle(): boolean;
  };
  readonly summons: {
    spawn(config: ShadeSummonConfig, x: number, y: number): string;
    destroy(instanceId: string): void;
    /** Returns IDs of all currently living shades (from any source). */
    getShadeIds(): string[];
  };
  /** Override for mirror distance in tiles. Injected by Clarity upgrade. */
  readonly getMirrorDistanceTiles?: () => number;
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface FractureSkillConfig {
  readonly cooldownMs: number;
  /** Fraction of player's base damage each shade deals (0–1). */
  readonly shadeDamageMultiplier: number;
  /** Formation radius in tiles. */
  readonly mirrorDistanceTiles: number;
  /** Shade movement speed (integration layer applies px/s). */
  readonly shadeSpeed: number;
  /** How much enemy aggro each shade draws (0–1). */
  readonly shadeAggroWeight: number;
  /** Tile size in pixels. Matches TILE_SIZE from Constants.ts. */
  readonly tileSize: number;
  /** Number of shades in the formation. */
  readonly shadeCount: number;
}

const DEFAULT_CONFIG: FractureSkillConfig = {
  cooldownMs: 2000,            // long cooldown tier
  shadeDamageMultiplier: 0.5,  // spec: 50% of player's base damage
  mirrorDistanceTiles: 3,      // shared with Tether base
  shadeSpeed: 1.0,             // integration layer applies px/s
  shadeAggroWeight: 0.0,       // shade is a projection, draws no aggro
  tileSize: 32,                // TILE_SIZE constant from Constants.ts
  shadeCount: 3,               // spec: splits into three
};

/** 120° in radians — angular separation between formation shades. */
const FORMATION_ANGLE_STEP = (2 * Math.PI) / 3;

/** Resolver scope — 'fracture' for upgrades with enhancesId: 'fracture'. */
const RESOLVER_SCOPE = 'fracture';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class FractureSkillEffect implements SkillEffect {
  readonly skillId = 'fracture';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly deps: FractureDeps;
  private readonly config: FractureSkillConfig;

  private _cooldownRemaining = 0;
  private _shadeIds: string[] = [];
  private _fizzled = false;
  private _activationCount = 0;
  private _resolvedDamage = 0;
  private _destroyedPriorShadeCount = 0;
  private _lastSpawnPositions: Array<{ x: number; y: number }> = [];

  constructor(deps: FractureDeps, config: Partial<FractureSkillConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters (observable by scenarios and integration layer) ---

  get cooldown(): number { return this._cooldownRemaining; }
  get shadeIds(): readonly string[] { return this._shadeIds; }
  get shadeCount(): number { return this._shadeIds.length; }
  get fizzled(): boolean { return this._fizzled; }
  get activationCount(): number { return this._activationCount; }
  get resolvedDamage(): number { return this._resolvedDamage; }
  get destroyedPriorShadeCount(): number { return this._destroyedPriorShadeCount; }
  get lastSpawnPositions(): ReadonlyArray<{ x: number; y: number }> { return this._lastSpawnPositions; }

  // --- Integration layer hook ---

  /**
   * Called by the integration layer when a shade is destroyed externally
   * (e.g., combat damage, player death). Removes the shade from tracking.
   */
  onShadeDestroyed(shadeId: string): void {
    const idx = this._shadeIds.indexOf(shadeId);
    if (idx >= 0) {
      this._shadeIds.splice(idx, 1);
    }
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Freshness fizzle check — consumes cooldown but does not spawn
    this._fizzled = this.deps.targeting.shouldFizzle();
    if (this._fizzled) {
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    // Destroy any existing shades (from Tether, Mirror, or a prior Fracture)
    const existingIds = this.deps.summons.getShadeIds();
    this._destroyedPriorShadeCount = existingIds.length;
    for (const id of existingIds) {
      this.deps.summons.destroy(id);
    }
    this._shadeIds = [];

    const pos = this.deps.player.getPosition();
    const facing = this.deps.player.getFacingDirection();
    const baseDamage = this.deps.player.getBaseDamage();

    // Resolve shade damage multiplier (Splinter raises 0.5 → 0.65 via max)
    const mult = this.deps.resolver.getMaxEffect(RESOLVER_SCOPE, 'shadeDamageMultiplier')
      ?? this.config.shadeDamageMultiplier;
    this._resolvedDamage = baseDamage * mult;

    // Resolve mirror distance (Clarity provides cycling override)
    const mirrorDist = this.deps.getMirrorDistanceTiles?.()
      ?? this.config.mirrorDistanceTiles;

    // Resolve formation radius multiplier (Splinter: 1.25×)
    const angleSpread = this.deps.resolver.getMultiplicativeEffect(RESOLVER_SCOPE, 'mirrorAngleSpread')
      || 1.0;
    const effectiveRadius = mirrorDist * angleSpread * this.config.tileSize;

    // Compute player facing angle for formation geometry
    const facingAngleRad = Math.atan2(facing.y, facing.x);

    this._lastSpawnPositions = [];

    for (let i = 0; i < this.config.shadeCount; i++) {
      const angle = facingAngleRad + (i * FORMATION_ANGLE_STEP);
      const spawnX = pos.x + Math.cos(angle) * effectiveRadius;
      const spawnY = pos.y + Math.sin(angle) * effectiveRadius;

      this._lastSpawnPositions.push({ x: spawnX, y: spawnY });

      const shadeConfig: ShadeSummonConfig = {
        id: `fracture_shade_${i}`,
        archetype: 'shade',
        baseHp: 9999,  // shade has no independent HP; damage routes to player
        baseDamage: this._resolvedDamage,
        baseSpeed: this.config.shadeSpeed,
        leashRadiusTiles: 0,
        aggroWeight: this.config.shadeAggroWeight,
        // TODO: define 'formation' AI mode in shade system (ShadeSummonConfig.aiMode union)
        aiMode: 'formation' as ShadeSummonConfig['aiMode'],
        shadeDamageMultiplier: mult,
        mirrorDistanceTiles: mirrorDist,
        sharedHpPool: true,
      };

      const shadeId = this.deps.summons.spawn(shadeConfig, spawnX, spawnY);
      this._shadeIds.push(shadeId);
    }

    this._activationCount++;
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  deactivate(): void {
    // Instant skill — no toggle state
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    for (const id of this._shadeIds) {
      this.deps.summons.destroy(id);
    }
    this._shadeIds = [];
    this._cooldownRemaining = 0;
  }
}
